import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Bell, Flame, TrendingUp, Phone, Users, Mail, BellRing, Calendar } from "lucide-react";
import { useNotificationPreferences, useUpdateNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { usePushNotifications } from "@/hooks/usePushNotifications";

export function NotificationPreferences() {
  const { data: preferences, isLoading } = useNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();
  const { isSupported, isSubscribed, isLoading: pushLoading, subscribe, unsubscribe, permission } = usePushNotifications();

  const handleToggle = (key: string, value: boolean) => {
    updatePreferences.mutate({ [key]: value });
  };

  const handleThresholdChange = (value: number[]) => {
    updatePreferences.mutate({ hot_lead_threshold: value[0] });
  };

  const handleReminderChange = (value: number[]) => {
    updatePreferences.mutate({ call_reminder_minutes: value[0] });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent className="space-y-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notification Preferences
        </CardTitle>
        <CardDescription>
          Customize which alerts you receive and their settings
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Push Notifications */}
        {isSupported && (
          <div className="flex items-center justify-between p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-center gap-3">
              <BellRing className="h-5 w-5 text-primary" />
              <div>
                <Label className="text-base font-medium">Browser Push Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  {isSubscribed 
                    ? "Receive notifications even when the app is in background"
                    : permission === 'denied' 
                      ? "Permission denied. Enable in browser settings."
                      : "Get instant browser notifications for important alerts"
                  }
                </p>
              </div>
            </div>
            <Button
              variant={isSubscribed ? "outline" : "default"}
              size="sm"
              onClick={isSubscribed ? unsubscribe : subscribe}
              disabled={pushLoading || permission === 'denied'}
            >
              {pushLoading ? "..." : isSubscribed ? "Disable" : "Enable"}
            </Button>
          </div>
        )}

        {/* Email Master Toggle */}
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-primary" />
            <div>
              <Label className="text-base font-medium">Email Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Enable or disable all email notifications
              </p>
            </div>
          </div>
          <Switch
            checked={preferences?.email_enabled ?? true}
            onCheckedChange={(checked) => handleToggle("email_enabled", checked)}
          />
        </div>

        <div className="space-y-6">
          {/* Hot Lead Alerts */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-orange-500/10">
                  <Flame className="h-4 w-4 text-orange-500" />
                </div>
                <div>
                  <Label className="text-base font-medium">Hot Lead Alerts</Label>
                  <p className="text-sm text-muted-foreground">
                    Get notified when leads reach high conversion scores
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences?.hot_lead_alerts ?? true}
                onCheckedChange={(checked) => handleToggle("hot_lead_alerts", checked)}
                disabled={!preferences?.email_enabled}
              />
            </div>
            
            {preferences?.hot_lead_alerts && preferences?.email_enabled && (
              <div className="ml-11 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Score threshold</span>
                  <span className="font-medium">{preferences?.hot_lead_threshold ?? 80}+</span>
                </div>
                <Slider
                  value={[preferences?.hot_lead_threshold ?? 80]}
                  onValueCommit={handleThresholdChange}
                  min={50}
                  max={95}
                  step={5}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Leads scoring {preferences?.hot_lead_threshold ?? 80} or higher will trigger an alert
                </p>
              </div>
            )}
          </div>

          {/* Deal Stage Changes */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-500/10">
                <TrendingUp className="h-4 w-4 text-blue-500" />
              </div>
              <div>
                <Label className="text-base font-medium">Deal Stage Changes</Label>
                <p className="text-sm text-muted-foreground">
                  Get notified when deals move to new stages
                </p>
              </div>
            </div>
            <Switch
              checked={preferences?.deal_stage_changes ?? true}
              onCheckedChange={(checked) => handleToggle("deal_stage_changes", checked)}
              disabled={!preferences?.email_enabled}
            />
          </div>

          {/* Scheduled Call Reminders */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-purple-500/10">
                  <Phone className="h-4 w-4 text-purple-500" />
                </div>
                <div>
                  <Label className="text-base font-medium">Call Reminders</Label>
                  <p className="text-sm text-muted-foreground">
                    Get reminded before scheduled calls
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences?.scheduled_call_reminders ?? true}
                onCheckedChange={(checked) => handleToggle("scheduled_call_reminders", checked)}
                disabled={!preferences?.email_enabled}
              />
            </div>
            
            {preferences?.scheduled_call_reminders && preferences?.email_enabled && (
              <div className="ml-11 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Remind me</span>
                  <span className="font-medium">{preferences?.call_reminder_minutes ?? 30} minutes before</span>
                </div>
                <Slider
                  value={[preferences?.call_reminder_minutes ?? 30]}
                  onValueCommit={handleReminderChange}
                  min={5}
                  max={60}
                  step={5}
                  className="w-full"
                />
              </div>
            )}
          </div>

          {/* New Lead Alerts */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-500/10">
                <Users className="h-4 w-4 text-green-500" />
              </div>
              <div>
                <Label className="text-base font-medium">New Lead Alerts</Label>
                <p className="text-sm text-muted-foreground">
                  Get notified when new leads are added
                </p>
              </div>
            </div>
            <Switch
              checked={preferences?.new_lead_alerts ?? false}
              onCheckedChange={(checked) => handleToggle("new_lead_alerts", checked)}
              disabled={!preferences?.email_enabled}
            />
          </div>

          {/* Daily Digest */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-indigo-500/10">
                <Calendar className="h-4 w-4 text-indigo-500" />
              </div>
              <div>
                <Label className="text-base font-medium">Daily Digest</Label>
                <p className="text-sm text-muted-foreground">
                  Receive a daily summary of activities and updates
                </p>
              </div>
            </div>
            <Switch
              checked={(preferences as any)?.daily_digest_enabled ?? false}
              onCheckedChange={(checked) => handleToggle("daily_digest_enabled", checked)}
              disabled={!preferences?.email_enabled}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
